import React from 'react';
import toast from 'react-hot-toast';
import { useI18n } from '../../../contexts/I18nContext';
import { composeLyrics, OPTIONS } from './mvData';
import useMvDictionaries from './useMvDictionaries';
import useMvCredits from './useMvCredits';
import useMvGenerator from './useMvGenerator';
import DependencySummary from './parts/DependencySummary';
import EmptyState from './parts/EmptyState';
import GenerateButton from './parts/GenerateButton';
import ImagePreviewStrip from './parts/ImagePreviewStrip';
import LyricsEditor from './parts/LyricsEditor';
import MusicControls from './parts/MusicControls';
import MusicResult from './parts/MusicResult';
import MvResult from './parts/MvResult';
import ProgressStepper from './parts/ProgressStepper';
import SettingsPanel from './parts/SettingsPanel';
import useElementHeight from './parts/useElementHeight';
import './MvGenerator.scss';

export default function MvGenerator() {
  const { t } = useI18n();
  const vm = useMvGenerator();
  const dictionaries = useMvDictionaries();
  const costs = useMvCredits(vm.values);
  const rootRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const panelHeight = useElementHeight(panelRef);
  const { error, clearError, applyOptionDefaults } = vm;
  const action = {
    lyrics: vm.generateLyrics,
    music: vm.generateMusic,
    video: vm.generateMv,
  }[vm.step];

  React.useEffect(() => {
    if (!error) return;
    toast.error(error);
    clearError();
  }, [error, clearError]);

  React.useEffect(() => {
    applyOptionDefaults(dictionaries.options);
  }, [dictionaries.options, applyOptionDefaults]);

  const selectStepFromProgress = (step) => {
    if (step === vm.step) return;
    vm.setStep(step);
  };

  const nextStep = () => {
    vm.nextStep();
    requestAnimationFrame(() => scrollToGenerator(rootRef.current));
  };

  return (
    <section className="mv-generator" ref={rootRef}>
      <div className="mv-generator__grid">
        <div className="mv-panel" ref={panelRef}>
          <ProgressStepper vm={vm} t={t} onSelect={selectStepFromProgress} />
          {vm.step === 'lyrics' && <LyricsControls vm={vm} t={t} dictionaries={dictionaries} />}
          {vm.step === 'music' && <MusicControls vm={vm} t={t} dictionaries={dictionaries} />}
          {vm.step === 'video' && <VideoControls vm={vm} t={t} />}
          <PanelAction vm={vm} action={action} costs={costs} nextStep={nextStep} t={t} />
        </div>
        <div className="mv-preview" style={panelHeight ? { '--mv-panel-height': `${panelHeight}px` } : undefined}>
          <DependencySummary vm={vm} t={t} />
          {vm.step === 'lyrics' && <LyricsPreview vm={vm} t={t} cost={costs.lyrics} />}
          {vm.step === 'music' && <MusicResult music={vm.music} t={t} cost={costs.music} onRegenerate={() => vm.startRegenerate('music')} />}
          {vm.step === 'video' && <MvResult mv={vm.mv} t={t} cost={costs.video} onRegenerate={() => vm.startRegenerate('video')} />}
        </div>
      </div>
    </section>
  );
}

function scrollToGenerator(element) {
  if (!element) return;
  const headerHeight = document.querySelector('.site-header')?.offsetHeight || 80;
  const target = element.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({ top: Math.max(0, target - headerHeight - 16), behavior: 'smooth' });
}

function PanelAction({ vm, action, costs, nextStep, t }) {
  if (!vm.showGenerate && vm.step !== 'video') {
    return <button className="mv-generate is-ready" onClick={nextStep}>{t(`videoGeneration.mv.actions.${vm.step === 'lyrics' ? 'nextMusic' : 'nextMv'}`)}</button>;
  }
  if (!vm.showGenerate) return null;
  return (
    <div className="mv-panel-action">
      <GenerateButton
        label={t(`videoGeneration.mv.generator.${vm.step}.button`)}
        cost={costs[vm.step]}
        isGenerating={vm.loading}
        progress={vm.generationProgress}
        disabled={!vm.canGenerate}
        onClick={action}
        t={t}
      />
    </div>
  );
}

function LyricsControls({ vm, t, dictionaries }) {
  const groups = [
    { title: t('videoGeneration.mv.generator.lyrics.genre'), items: dictionaries.options.style, value: vm.values.style, onChange: (v) => vm.updateValue('style', v) },
    { title: t('videoGeneration.mv.generator.lyrics.mood'), items: dictionaries.options.mood, value: vm.values.mood, onChange: (v) => vm.updateValue('mood', v) },
    { title: t('videoGeneration.mv.generator.lyrics.vocal'), items: dictionaries.options.vocal, value: vm.values.vocal, onChange: (v) => vm.updateValue('vocal', v) },
    { title: t('videoGeneration.mv.generator.lyrics.language'), items: dictionaries.options.language, value: vm.values.language, onChange: (v) => vm.updateValue('language', v) },
  ];
  return (
    <>
      <div className="mv-field">
        <label>{t('videoGeneration.mv.generator.lyrics.idea')}</label>
        <textarea value={vm.values.idea} onChange={(e) => vm.updateValue('idea', e.target.value)} placeholder={t('videoGeneration.mv.generator.lyrics.placeholder')} />
      </div>
      <SettingsPanel title={t('videoGeneration.mv.generator.lyrics.settings')} groups={groups} t={t} />
    </>
  );
}

function VideoControls({ vm, t }) {
  const groups = [
    { title: t('videoGeneration.mv.generator.video.ratio'), items: OPTIONS.ratio, value: vm.values.ratio, onChange: (v) => vm.updateValue('ratio', v) },
    { title: t('videoGeneration.mv.generator.video.resolution'), items: OPTIONS.resolution, value: vm.values.resolution, onChange: (v) => vm.updateValue('resolution', v) },
    { title: t('videoGeneration.mv.generator.video.visual'), items: OPTIONS.visual, value: vm.values.visual, onChange: (v) => vm.updateValue('visual', v) },
  ];
  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) vm.setImages((prev) => [...prev, ...files]);
    e.target.value = '';
  };
  const removeImage = (index) => {
    vm.setImages((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };
  return (
    <>
      <div className="mv-upload">
        <input type="file" accept="image/*" multiple onChange={onFiles} />
        <strong>{t('videoGeneration.mv.generator.video.upload')}</strong>
        <span>{vm.images.length ? t('videoGeneration.mv.generator.video.selected', { count: vm.images.length }) : t('videoGeneration.mv.generator.video.formats')}</span>
      </div>
      <ImagePreviewStrip images={vm.images} onRemove={removeImage} />
      <div className="mv-field">
        <label>{t('videoGeneration.mv.generator.video.prompt')}</label>
        <textarea value={vm.values.mvPrompt} onChange={(e) => vm.updateValue('mvPrompt', e.target.value)} placeholder={t('videoGeneration.mv.generator.video.placeholder')} />
      </div>
      <SettingsPanel title={t('videoGeneration.mv.generator.video.settings')} groups={groups} t={t} />
    </>
  );
}

function LyricsPreview({ vm, t, cost }) {
  if (!vm.lyrics.length) {
    return (
      <EmptyState
        type="lyrics"
        title={t('videoGeneration.mv.preview.emptyLyricsTitle')}
        desc={t('videoGeneration.mv.preview.emptyLyrics')}
        hint={t('videoGeneration.mv.preview.emptyLyricsHint')}
      />
    );
  }
  const copy = async () => {
    try {
      await navigator.clipboard?.writeText(composeLyrics(vm.lyrics));
      toast.success(t('videoGeneration.mv.actions.copySuccess'));
    } catch (error) {
      toast.error(t('videoGeneration.mv.actions.copyFailed'));
    }
  };
  return (
    <LyricsEditor
      sections={vm.lyrics}
      isEditing={vm.isEditingLyrics}
      onChange={vm.setLyrics}
      onCopy={copy}
      onToggleEdit={vm.toggleLyricsEditing}
      onRegenerate={() => vm.startRegenerate('lyrics')}
      cost={cost}
      t={t}
    />
  );
}
